/**
 * ListActionsPanel — zakładka „Akcje" w Kreatorze Listy (PHASE 25).
 *
 * Warstwa DRUGA modelu akcji zbiorczych: WYBÓR per layout, co pokazać w menu
 * „Akcje" tej listy. Katalog (co dana kartoteka w ogóle umie) żyje w kodzie
 * (`@saas/shared` — `actionsForKartoteka`), tutaj tenant tylko włącza/wyłącza i
 * ustawia kolejność. Zapis do `list_config.listActions` (`{actionId, order}[]`);
 * resolver runtime (`resolveListActions`) czyta dokładnie to i przecina z
 * aktywnymi modułami + uprawnieniami usera.
 *
 * Jedno miejsce dla wszystkich list: ten panel działa dla kartotek systemowych
 * (kluczowanych `kartotekaKey`) ORAZ dla list generycznych (`'generic'` — moduł
 * własny tenanta / moduł systemowy z Kreatora u ITE), gdzie katalog pokazuje
 * tylko akcje uniwersalne (archiwizuj/przywróć/eksport/import/odśwież).
 *
 * Zapis przez `useTableLayout(slug)` (PUT — zachowuje kolumny/sort/filtry/
 * quickFilters, podmienia wyłącznie `listActions`).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  actionsForKartoteka,
  defaultListActionIdsFor,
  type ListActionConfig,
  type ListActionDef,
  type ListActionDefinitionDTO,
  type ListActionId,
  type ListActionKartotekaKey,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { GripVertical, Plus, RotateCcw, Save, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { renderIconByName } from '@/components/modules/IconPicker';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import { listActionLucideIcon } from './ListActionsMenu';
import { useTableLayout } from './useTableLayout';

/** Kolejność katalogowa akcji — do stabilnego sortu listy „dostępne do dodania". */
function catalogOrder(applicable: readonly ListActionDef[]): Map<ListActionId, number> {
  const m = new Map<ListActionId, number>();
  applicable.forEach((def, i) => m.set(def.id, i));
  return m;
}

/** Domyślny zestaw dla tej kartoteki: akcje domyślne katalogu ∩ dostępne dla kartoteki. */
function defaultEnabledFor(
  applicable: readonly ListActionDef[],
  kartotekaKey: ListActionKartotekaKey,
  documentClass: string | undefined,
): ListActionConfig[] {
  const applicableIds = new Set(applicable.map((d) => d.id));
  return defaultListActionIdsFor(kartotekaKey, documentClass)
    .filter((id) => applicableIds.has(id))
    .map((actionId, order) => ({ actionId, order }));
}

export function ListActionsPanel({
  slug,
  kartotekaKey,
  documentClass,
}: {
  slug: string;
  kartotekaKey: ListActionKartotekaKey;
  documentClass?: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const { layout, isLoading, saveLayout, isSaving } = useTableLayout(slug);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Akcje z katalogu dostępne dla tej kartoteki (paleta) — źródło prawdy o tym,
  // co w ogóle wolno włączyć. Dla listy generycznej to same akcje uniwersalne.
  const applicable = useMemo(
    () => actionsForKartoteka(kartotekaKey, documentClass),
    [kartotekaKey, documentClass],
  );
  const applicableById = useMemo(() => {
    const m = new Map<ListActionId, ListActionDef>();
    for (const def of applicable) m.set(def.id, def);
    return m;
  }, [applicable]);
  const order = useMemo(() => catalogOrder(applicable), [applicable]);

  // Włączone akcje (kolejność = kolejność w menu). `null` = jeszcze nie wczytano.
  const [enabled, setEnabled] = useState<ListActionConfig[] | null>(null);
  // Snapshot wczytanego stanu — do wykrycia „brudnego" formularza (aktywacja Zapisz).
  const [initial, setInitial] = useState<string>('');

  useEffect(() => {
    if (isLoading) return;
    const saved = layout?.layoutConfig?.listActions;
    // Wczytany config: przefiltruj do wciąż-dostępnych (akcja mogła wypaść z
    // katalogu / przestać dotyczyć kartoteki) i posortuj po zapisanym `order`.
    const next: ListActionConfig[] =
      saved !== undefined
        ? [...saved]
            .filter((a) => applicableById.has(a.actionId))
            .sort((a, b) => a.order - b.order)
            .map((a, i) => ({ actionId: a.actionId, order: i }))
        : defaultEnabledFor(applicable, kartotekaKey, documentClass);
    setEnabled(next);
    setInitial(JSON.stringify(next));
    // Reagujemy tylko na pierwsze wczytanie / zmianę layoutu lub kartoteki.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, layout?.id, kartotekaKey, documentClass]);

  const enabledIds = useMemo(() => new Set((enabled ?? []).map((a) => a.actionId)), [enabled]);
  const available = useMemo(
    () =>
      applicable
        .filter((def) => !enabledIds.has(def.id))
        .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0)),
    [applicable, enabledIds, order],
  );

  // Akcje WŁASNE tenanta dla tej kartoteki (read-only w Kreatorze — zarządzane w
  // Ustawienia→Akcje; tu pokazujemy je pod kreską dla świadomości + chip „Własna").
  const customDefsQuery = useQuery({
    queryKey: ['list-action-definitions', kartotekaKey],
    queryFn: async () => {
      const res = await api.get<{ definitions: ListActionDefinitionDTO[] }>(
        '/list-action-definitions',
        { params: { kartoteka: kartotekaKey } },
      );
      return res.data.definitions;
    },
  });
  const customDefs = (customDefsQuery.data ?? []).filter((d) => d.isActive);

  const dirty = enabled !== null && JSON.stringify(enabled) !== initial;

  function reseq(list: ListActionConfig[]): ListActionConfig[] {
    return list.map((a, i) => ({ actionId: a.actionId, order: i }));
  }
  function addAction(id: ListActionId): void {
    setEnabled((prev) => reseq([...(prev ?? []), { actionId: id, order: 0 }]));
  }
  function removeAction(id: ListActionId): void {
    setEnabled((prev) => reseq((prev ?? []).filter((a) => a.actionId !== id)));
  }
  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setEnabled((prev) => {
      if (!prev) return prev;
      const oldIndex = prev.findIndex((a) => a.actionId === active.id);
      const newIndex = prev.findIndex((a) => a.actionId === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return reseq(arrayMove(prev, oldIndex, newIndex));
    });
  }
  function resetDefault(): void {
    setEnabled(defaultEnabledFor(applicable, kartotekaKey, documentClass));
  }

  async function handleSave(): Promise<void> {
    if (!enabled || !layout) return;
    await saveLayout({ ...layout.layoutConfig, listActions: enabled });
    setInitial(JSON.stringify(enabled));
    toast.show({ variant: 'success', title: t('tenant.listBuilder.actions.saved') });
  }

  if (isLoading || enabled === null) {
    return (
      <div className="p-4 text-sm text-text-tertiary" data-testid="list-actions-panel-loading">
        {t('common.loading')}
      </div>
    );
  }

  function badges(def: ListActionDef): React.ReactNode {
    const chips: string[] = [];
    if (def.moduleCode) chips.push(t('tenant.listBuilder.actions.badge.module'));
    if (def.permission || def.permissionAnyOf)
      chips.push(t('tenant.listBuilder.actions.badge.permission'));
    if (def.destructive) chips.push(t('tenant.listBuilder.actions.badge.destructive'));
    if (chips.length === 0) return null;
    return (
      <span className="flex flex-wrap gap-1">
        {chips.map((c) => (
          <span
            key={c}
            className="rounded bg-bg-tertiary px-1.5 py-0.5 text-[11px] text-text-tertiary"
          >
            {c}
          </span>
        ))}
      </span>
    );
  }

  function ActionIcon({ def }: { def: ListActionDef }): React.ReactElement {
    const Icon = listActionLucideIcon(def.icon);
    return <Icon className="h-4 w-4 shrink-0 text-text-secondary" />;
  }

  // Chip zakresu (jak na ekranie Ustawienia→Akcje): niebieski „dla wybranych rekordów"
  // (requiresSelection) / szary „ogólna". Mówi, na czym akcja działa.
  function scopeChip(requiresSelection: boolean): React.ReactNode {
    return requiresSelection ? (
      <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
        {t('tenant.listActions.settings.onSelection')}
      </span>
    ) : (
      <span className="shrink-0 rounded-full bg-bg-tertiary px-2 py-0.5 text-[11px] text-text-tertiary">
        {t('tenant.listActions.settings.general')}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-1" data-testid="list-actions-panel">
      <div>
        <h3 className="text-sm font-semibold text-text-primary">
          {t('tenant.listBuilder.actions.title')}
        </h3>
        <p className="mt-0.5 text-xs text-text-tertiary">
          {t('tenant.listBuilder.actions.subtitle')}
        </p>
      </div>

      {/* Włączone (widoczne w menu Akcje) — z kolejnością */}
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t('tenant.listBuilder.actions.enabledHeading')}
          </h4>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RotateCcw className="h-3.5 w-3.5" />}
            onClick={resetDefault}
            data-testid="list-actions-reset"
          >
            {t('tenant.listBuilder.actions.resetDefault')}
          </Button>
        </div>
        {enabled.length === 0 ? (
          <p
            className="rounded-md border border-dashed border-border-primary p-3 text-xs text-text-tertiary"
            data-testid="list-actions-enabled-empty"
          >
            {t('tenant.listBuilder.actions.emptyEnabled')}
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={enabled.map((e) => e.actionId)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="flex flex-col gap-1.5" data-testid="list-actions-enabled">
                {enabled.map((entry) => {
                  const def = applicableById.get(entry.actionId);
                  if (!def) return null;
                  return (
                    <EnabledActionRow
                      key={entry.actionId}
                      id={entry.actionId}
                      icon={<ActionIcon def={def} />}
                      label={t(def.labelKey)}
                      badges={
                        <>
                          {scopeChip(def.requiresSelection)}
                          {badges(def)}
                        </>
                      }
                      dragAria={t('tenant.listBuilder.actions.dragAria')}
                      removeAria={t('tenant.listBuilder.actions.remove')}
                      onRemove={() => removeAction(entry.actionId)}
                    />
                  );
                })}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </section>

      {/* Dostępne do dodania */}
      <section className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
          {t('tenant.listBuilder.actions.availableHeading')}
        </h4>
        {available.length === 0 ? (
          <p className="text-xs text-text-tertiary" data-testid="list-actions-available-empty">
            {t('tenant.listBuilder.actions.emptyAvailable')}
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5" data-testid="list-actions-available">
            {available.map((def) => (
              <li
                key={def.id}
                className="flex items-center gap-2 rounded-md border border-dashed border-border-primary px-2.5 py-2"
                data-testid={`list-actions-available-${def.id}`}
              >
                <ActionIcon def={def} />
                <span className="flex-1 text-sm text-text-secondary">{t(def.labelKey)}</span>
                {scopeChip(def.requiresSelection)}
                {badges(def)}
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus className="h-3.5 w-3.5" />}
                  onClick={() => addAction(def.id)}
                  data-testid={`list-actions-add-${def.id}`}
                >
                  {t('tenant.listBuilder.actions.add')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Akcje WŁASNE tenanta — pod kreską, read-only (zarządzane w Ustawienia→Akcje). */}
      {customDefs.length > 0 && (
        <section
          className="flex flex-col gap-2 border-t border-border-primary pt-4"
          data-testid="list-actions-custom"
        >
          <h4 className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
            {t('tenant.listActions.settings.customActions')}
          </h4>
          <p className="text-xs text-text-tertiary">
            {t('tenant.listBuilder.actions.customManagedHint')}
          </p>
          <ul className="flex flex-col gap-1.5">
            {customDefs.map((d) => (
              <li
                key={d.id}
                className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-2.5 py-2"
                data-testid={`list-actions-custom-${d.id}`}
              >
                <span className="shrink-0 text-text-secondary" aria-hidden="true">
                  {renderIconByName(d.icon ?? 'Zap', 'h-4 w-4')}
                </span>
                <span className="flex-1 text-sm text-text-primary">{d.name}</span>
                {scopeChip(true)}
                <span className="shrink-0 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                  {t('tenant.listBuilder.actions.ownChip')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex justify-end border-t border-border-primary pt-3">
        <Button
          variant="primary"
          leftIcon={<Save className="h-4 w-4" />}
          onClick={handleSave}
          loading={isSaving}
          disabled={!dirty}
          data-testid="list-actions-save"
        >
          {t('tenant.listBuilder.actions.save')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Pojedynczy sortowalny wiersz akcji włączonej w menu. Kolejność zmienia się
 * przeciągnięciem za uchwyt (dnd-kit); krzyżek usuwa akcję z menu (wraca do
 * „Dostępne do dodania").
 */
function EnabledActionRow({
  id,
  icon,
  label,
  badges,
  dragAria,
  removeAria,
  onRemove,
}: {
  id: string;
  icon: React.ReactNode;
  label: string;
  badges: React.ReactNode;
  dragAria: string;
  removeAria: string;
  onRemove: () => void;
}): React.ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-2.5 py-2"
      data-testid={`list-actions-enabled-${id}`}
    >
      <IconButton
        size="sm"
        aria-label={dragAria}
        className="cursor-grab touch-none active:cursor-grabbing"
        data-testid={`list-actions-drag-${id}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical />
      </IconButton>
      {icon}
      <span className="flex-1 text-sm text-text-primary">{label}</span>
      {badges}
      <IconButton
        size="sm"
        variant="danger"
        aria-label={removeAria}
        onClick={onRemove}
        data-testid={`list-actions-remove-${id}`}
      >
        <X />
      </IconButton>
    </li>
  );
}
